/**
 * SentimentBadge (DC-1573 Half A)
 *
 * Pill-shaped chip for the three sentiment-depth signals that fan out across
 * the OrgSentimentHeatmap surface — variance, sample size, and consensus.
 *
 * Each variant carries its own three-state palette (low / mid / high) so a
 * reader can scan a row of badges and triage by color before reading values.
 * Thresholds are tuned to the aggregator's empirically-seen distributions:
 *   - variance .15 / .35     (calm → mixed → divided)
 *   - sample-size 3 / 8      (anecdotal → indicative → confident)
 *   - consensus .5 / .8      (fragmented → leaning → aligned)
 *
 * Visual language follows triangulation/CoverageChip.tsx; styling lives in
 * the sibling CSS file so light + dark themes share one source of truth.
 */

import React from 'react';
import './sentiment-badge.css';

export type SentimentBadgeVariant = 'variance' | 'sample-size' | 'consensus';
type Level = 'low' | 'mid' | 'high';

interface SentimentBadgeProps {
  variant: SentimentBadgeVariant;
  /** Raw value. Variance/consensus expect 0..1; sample-size expects a positive integer. */
  value: number;
  /** Optional denominator for sample-size badges. Renders "n/N" when provided. */
  total?: number;
  className?: string;
}

const VARIANCE_RUBRIC =
  'Variance across personas — low (under 0.15) is tight consensus, mid (0.15–0.35) is mixed views, high (over 0.35) is sharply divided.';
const SAMPLE_RUBRIC =
  'Sample size — fewer than 3 responses is anecdotal, 3–7 indicative, 8 or more confident.';
const CONSENSUS_RUBRIC =
  'Consensus across personas — under 50% is fragmented, 50–80% leaning aligned, above 80% strong consensus.';

function classifyVariance(value: number): Level {
  if (value <= 0.15) return 'low';
  if (value <= 0.35) return 'mid';
  return 'high';
}

function classifySample(value: number): Level {
  if (value < 3) return 'low';
  if (value < 8) return 'mid';
  return 'high';
}

function classifyConsensus(value: number): Level {
  if (value < 0.5) return 'low';
  if (value < 0.8) return 'mid';
  return 'high';
}

function classify(variant: SentimentBadgeVariant, value: number): Level {
  if (variant === 'variance') return classifyVariance(value);
  if (variant === 'sample-size') return classifySample(value);
  return classifyConsensus(value);
}

function formatVariance(value: number, sample: number | undefined): string {
  if (typeof sample === 'number' && sample < 2) return '—';
  if (!Number.isFinite(value)) return '—';
  return value.toFixed(2);
}

function formatConsensus(value: number): string {
  if (!Number.isFinite(value)) return '—';
  return `${Math.round(value * 100)}%`;
}

function formatSample(value: number, total: number | undefined): string {
  const intValue = Math.max(0, Math.round(value));
  if (typeof total === 'number' && total > 0) {
    return `${intValue}/${Math.round(total)}`;
  }
  return `n=${intValue}`;
}

export const SentimentBadge: React.FC<SentimentBadgeProps> = ({
  variant,
  value,
  total,
  className,
}) => {
  const level = classify(variant, value);
  const baseClass = `sentiment-badge sentiment-badge--${variantKey(variant)}-${level}`;
  const fullClass = className ? `${baseClass} ${className}` : baseClass;

  if (variant === 'variance') {
    return (
      <span
        data-testid="sentiment-badge"
        data-variant={variant}
        data-level={level}
        className={fullClass}
        title={VARIANCE_RUBRIC}
      >
        <span className="sentiment-badge__label">Var</span>
        <span className="sentiment-badge__value">{formatVariance(value, total)}</span>
      </span>
    );
  }

  if (variant === 'sample-size') {
    return (
      <span
        data-testid="sentiment-badge"
        data-variant={variant}
        data-level={level}
        className={fullClass}
        title={SAMPLE_RUBRIC}
      >
        <span className="sentiment-badge__value">{formatSample(value, total)}</span>
      </span>
    );
  }

  return (
    <span
      data-testid="sentiment-badge"
      data-variant={variant}
      data-level={level}
      className={fullClass}
      title={CONSENSUS_RUBRIC}
    >
      <span className="sentiment-badge__value">{formatConsensus(value)}</span>
      <span className="sentiment-badge__label">consensus</span>
    </span>
  );
};

function variantKey(variant: SentimentBadgeVariant): string {
  return variant === 'sample-size' ? 'sample' : variant;
}

export default SentimentBadge;
